import { defaultKeymap } from '@codemirror/commands';
import { syntaxHighlighting } from '@codemirror/language';
import { highlightSelectionMatches, search, searchKeymap } from '@codemirror/search';
import { EditorState, RangeSetBuilder, StateEffect, StateField } from '@codemirror/state';
import { Decoration, EditorView, drawSelection, highlightActiveLine, keymap, lineNumbers, type DecorationSet } from '@codemirror/view';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { editorTheme as theme, highlight } from '../lib/editorTheme';
import { languageFor } from '../lib/languages';
import { useWorkspace } from '../state/workspace';

const setTarget = StateEffect.define<number | null>();
const targetLine = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(value, tr) {
    for (const effect of tr.effects) {
      if (!effect.is(setTarget)) continue;
      if (effect.value === null) return Decoration.none;
      const builder = new RangeSetBuilder<Decoration>();
      const line = tr.state.doc.line(Math.min(Math.max(1, effect.value), tr.state.doc.lines));
      builder.add(line.from, line.from, Decoration.line({ class: 'mv-target-line' }));
      return builder.finish();
    }
    return value.map(tr.changes);
  },
  provide: (f) => EditorView.decorations.from(f),
});

export function SourceView({ path, line, nonce }: { path: string; line?: number; nonce?: number }) {
  const source = useWorkspace((s) => s.source);
  const lastChange = useWorkspace((s) => s.lastChange?.at);
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!source) return;
    let cancelled = false;
    source
      .read(path)
      .then((value) => !cancelled && setText(value.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n')))
      .catch((e: Error) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [source, path, lastChange]);

  useEffect(() => {
    if (text === null || !host.current) return;
    view.current?.destroy();
    view.current = new EditorView({
      parent: host.current,
      state: EditorState.create({
        doc: text,
        extensions: [
          lineNumbers(),
          highlightActiveLine(),
          drawSelection(),
          EditorState.readOnly.of(true),
          EditorView.editable.of(false),
          EditorView.contentAttributes.of({ tabindex: '0', 'aria-label': `Source of ${path}` }),
          languageFor(path),
          syntaxHighlighting(highlight),
          search({ top: true }),
          highlightSelectionMatches(),
          keymap.of([...searchKeymap, ...defaultKeymap]),
          targetLine,
          theme,
        ],
      }),
    });
    return () => {
      view.current?.destroy();
      view.current = null;
    };
  }, [text, path]);

  useEffect(() => {
    const v = view.current;
    if (!v || text === null) return;
    if (line === undefined) {
      v.dispatch({ effects: setTarget.of(null) });
      return;
    }
    const target = v.state.doc.line(Math.min(Math.max(1, line), v.state.doc.lines));
    v.dispatch({
      effects: [setTarget.of(line), EditorView.scrollIntoView(target.from, { y: 'center' })],
      selection: { anchor: target.from },
    });
  }, [line, nonce, text]);

  if (error) return <div className="p-6 text-muted">{error}</div>;
  return (
    <div className="relative h-full" data-testid="source-view">
      {text === null && (
        <div className="absolute inset-0 flex items-center justify-center gap-2 text-muted">
          <Loader2 size={16} className="animate-spin" /> Reading {path}…
        </div>
      )}
      <div ref={host} className="h-full" />
    </div>
  );
}
